<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
import { computed } from 'vue';
import { normalizeAvailabilityKey } from '@sera/core';

interface Props {
  availableDates: Record<string, unknown>;
  selectedDate?: string | null;
  currentYear: number;
  currentMonth: number;
  /**
   * Earliest selectable date in ISO `YYYY-MM-DD` (zero-padded) form. Any
   * day cell strictly before this date is rendered as unavailable, even if
   * the backend echoed it back in `availableDates`. Pass today-in-tenant-TZ
   * here (see `WizardStepDateTime.vue` / `RescheduleDialog.vue`).
   *
   * When null/undefined, no past-date floor is enforced — the calendar
   * falls back to the historical behaviour (cell availability driven
   * purely by `availableDates`).
   */
  minDate?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  selectedDate: null,
  minDate: null,
});

const emit = defineEmits<{
  select: [dateString: string];
  'month-change': [year: number, month: number];
}>();

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const monthLabel = computed(
  () => `${MONTH_NAMES[props.currentMonth - 1]} ${props.currentYear}`,
);

// Parse a minDate prop in canonical ISO `YYYY-MM-DD` (zero-padded) into a
// numeric (year, month, day) tuple. Returning a tuple lets the cell-level
// comparison sidestep lexicographic pitfalls with non-zero-padded calendar
// keys (e.g. `2026-1-5` vs `2026-10-1` would mis-sort as strings). Returns
// null when the prop is null/empty/malformed so the calendar falls back to
// "no floor" behaviour rather than throwing.
const minDateParts = computed<{ y: number; m: number; d: number } | null>(
  () => {
    if (!props.minDate) return null;
    const parts = props.minDate.split('-');
    if (parts.length !== 3) return null;
    const y = parseInt(parts[0]!, 10);
    const m = parseInt(parts[1]!, 10);
    const d = parseInt(parts[2]!, 10);
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
      return null;
    }
    return { y, m, d };
  },
);

function isBeforeMinDate(y: number, m: number, d: number): boolean {
  const floor = minDateParts.value;
  if (!floor) return false;
  if (y !== floor.y) return y < floor.y;
  if (m !== floor.m) return m < floor.m;
  return d < floor.d;
}

// Full-literal Tailwind class strings — never interpolated (CLAUDE.md constraint)
const todayYear = new Date().getFullYear();
const todayMonth = new Date().getMonth() + 1;

const isPrevDisabled = computed(
  () =>
    props.currentYear < todayYear ||
    (props.currentYear === todayYear && props.currentMonth <= todayMonth),
);

// Full-literal Tailwind class strings — never interpolated (CLAUDE.md constraint)
const CELL_BASE =
  'relative min-h-11 rounded-md text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary';
const CELL_SELECTED = 'bg-primary text-white';
const CELL_AVAILABLE =
  'bg-surface border border-border-strong hover:bg-primary-light text-foreground';
const CELL_UNAVAILABLE = 'text-subtle cursor-not-allowed opacity-50';
const CELL_TODAY = 'ring-1 ring-border-strong';

const DAY_HEADERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

interface Cell {
  day: number;
  date: string;
  inMonth: boolean;
  available: boolean;
  isSelected: boolean;
  isToday: boolean;
  key: string;
}

// Normalize incoming availableDates keys once at receive time so a zero-padded
// '2026-1-05' still resolves when the cell key is the canonical '2026-1-5'.
const normalizedAvailable = computed(() => {
  const out: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(props.availableDates)) {
    if (v === true) out[normalizeAvailabilityKey(k)] = true;
  }
  return out;
});

const normalizedSelected = computed<string | null>(() => {
  if (!props.selectedDate) return null;
  // selectedDate is an external prop. normalizeAvailabilityKey throws on
  // non-ISO input (e.g. "2026/05/01", "2026-05"), so a bad value would crash
  // this render computed. Degrade to no-selection instead, matching the
  // tolerant null-on-malformed behaviour of minDateParts above.
  try {
    return normalizeAvailabilityKey(props.selectedDate);
  } catch {
    return null;
  }
});

const todayKey = (() => {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
})();

const cells = computed<Cell[]>(() => {
  const year = props.currentYear;
  const month = props.currentMonth; // 1-12
  const firstOfMonth = new Date(year, month - 1, 1);
  const lastOfMonth = new Date(year, month, 0);
  const daysInMonth = lastOfMonth.getDate();
  const startWeekday = firstOfMonth.getDay(); // 0=Sun
  const totalCells = Math.ceil((startWeekday + daysInMonth) / 7) * 7;

  const result: Cell[] = [];
  for (let i = 0; i < totalCells; i++) {
    const dayNum = i - startWeekday + 1;
    const inMonth = dayNum >= 1 && dayNum <= daysInMonth;
    if (inMonth) {
      const date = normalizeAvailabilityKey(`${year}-${month}-${dayNum}`);
      // Past-date floor: a cell strictly before minDate is treated as
      // unavailable regardless of what the backend returned. Defence in
      // depth against stale cache, day-boundary races, and customer-local
      // vs tenant-TZ midnight drift.
      const beforeFloor = isBeforeMinDate(year, month, dayNum);
      const available =
        !beforeFloor && normalizedAvailable.value[date] === true;
      // wizard-past-date-selectable cycle 2: a cell is only considered the
      // active selection when it is ALSO available. Without this guard, a
      // caller that programmatically seeds `selectedDate` to a past or
      // unavailable date would render the cell with `CELL_SELECTED`
      // (bg-primary), making it look like the active pick even though
      // clicking it is rejected by `onCellSelect` (no-op when !available).
      // Defence in depth so the rendered selection always matches what the
      // user can actually submit.
      const isSelected = available && normalizedSelected.value === date;
      const isToday = todayKey === date;
      result.push({
        day: dayNum,
        date,
        inMonth,
        available,
        isSelected,
        isToday,
        key: date,
      });
    } else {
      result.push({
        day: 0,
        date: '',
        inMonth: false,
        available: false,
        isSelected: false,
        isToday: false,
        key: `out-${i}`,
      });
    }
  }
  return result;
});

function classFor(cell: Cell): string {
  if (!cell.inMonth) return CELL_BASE;
  let cls = CELL_BASE;
  if (cell.isSelected) cls += ' ' + CELL_SELECTED;
  else if (cell.available) cls += ' ' + CELL_AVAILABLE;
  else cls += ' ' + CELL_UNAVAILABLE;
  if (cell.isToday) cls += ' ' + CELL_TODAY;
  return cls;
}

function onCellSelect(cell: Cell): void {
  if (!cell.inMonth || !cell.available) return;
  emit('select', cell.date);
}

function onPrevMonth(): void {
  if (isPrevDisabled.value) return;
  const m = props.currentMonth - 1;
  if (m < 1) emit('month-change', props.currentYear - 1, 12);
  else emit('month-change', props.currentYear, m);
}

function onNextMonth(): void {
  const m = props.currentMonth + 1;
  if (m > 12) emit('month-change', props.currentYear + 1, 1);
  else emit('month-change', props.currentYear, m);
}

function focusCellAt(currentIndex: number, delta: number): void {
  const target = currentIndex + delta;
  if (target < 0 || target >= cells.value.length) return;
  const el = document.querySelector<HTMLButtonElement>(
    `[data-cell-index="${target}"]`,
  );
  el?.focus();
}
</script>

<template>
  <div>
    <header class="flex items-center justify-between mb-2">
      <button
        type="button"
        class="px-2 py-1 rounded-md text-foreground hover:bg-canvas focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        :class="isPrevDisabled ? 'opacity-30 cursor-not-allowed' : ''"
        :disabled="isPrevDisabled"
        :aria-disabled="isPrevDisabled ? 'true' : 'false'"
        aria-label="Previous month"
        data-testid="calendar-prev-month"
        @click="onPrevMonth"
      >
        &lsaquo;
      </button>
      <span
        class="text-base font-medium text-foreground"
        data-testid="calendar-month-label"
        >{{ monthLabel }}</span
      >
      <button
        type="button"
        class="px-2 py-1 rounded-md text-foreground hover:bg-canvas focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        aria-label="Next month"
        data-testid="calendar-next-month"
        @click="onNextMonth"
      >
        &rsaquo;
      </button>
    </header>

    <div role="grid" class="grid grid-cols-7 gap-1">
      <div
        v-for="(d, i) in DAY_HEADERS"
        :key="`hdr-${i}`"
        data-testid="calendar-day-header"
        class="text-center text-xs text-muted py-1"
      >
        {{ d }}
      </div>
      <button
        v-for="(cell, idx) in cells"
        :key="cell.key"
        type="button"
        role="gridcell"
        :class="classFor(cell)"
        :data-cell-index="idx"
        :data-testid="
          cell.inMonth ? `calendar-cell-${cell.date}` : 'calendar-cell-outside'
        "
        :aria-selected="cell.isSelected ? 'true' : 'false'"
        :aria-disabled="!cell.inMonth || !cell.available ? 'true' : 'false'"
        :disabled="!cell.inMonth || !cell.available"
        :tabindex="cell.inMonth && cell.available ? 0 : -1"
        @click="onCellSelect(cell)"
        @keydown.enter.prevent="onCellSelect(cell)"
        @keydown.space.prevent="onCellSelect(cell)"
        @keydown.right.prevent="focusCellAt(idx, 1)"
        @keydown.left.prevent="focusCellAt(idx, -1)"
        @keydown.up.prevent="focusCellAt(idx, -7)"
        @keydown.down.prevent="focusCellAt(idx, 7)"
      >
        <span v-if="cell.inMonth">{{ cell.day }}</span>
        <span
          v-if="cell.inMonth && cell.available"
          class="absolute bottom-0.5 left-1/2 -translate-x-1/2 size-1 rounded-full"
          :class="cell.isSelected ? 'bg-white/60' : 'bg-success'"
          aria-hidden="true"
        />
      </button>
    </div>
  </div>
</template>
